/**
 * Jewelry customization profiles — locket photos, chain lengths, ring/bracelet sizes, engraving.
 * Cart/order stores compact JSON prefixed with `j:` (see serializeJewelryCustomization).
 */

import { normalizePersonalizationLetter, formatCharCustomizationLabel, productCharPersonalizationEnabled } from '../productPersonalization';
import { productMultiPieceEnabled } from '../pieceCustomization/pieceConfig';
import { BUILTIN_JEWELRY_PROFILES } from './jewelryBuiltinProfiles';
import { getJewelryProfile } from './jewelryProfileRegistry';
import {
  getStoreJewelryOptions,
  resolveProductStoreListOverride,
  slugifyStoreListId,
} from './jewelryStoreOptionLists';

export { BUILTIN_JEWELRY_PROFILES } from './jewelryBuiltinProfiles';
export { getJewelryProfile, getJewelryProfileIds, getJewelryProfiles } from './jewelryProfileRegistry';
export {
  DEFAULT_BRACELET_SIZES,
  DEFAULT_CHAIN_OPTIONS,
  DEFAULT_RING_SIZES,
  BUILTIN_STORE_LIST_SEEDS,
  collectReferencedStoreListIds,
  emptyStoreOptionLists,
  getBuiltinJewelryOptions,
  getJewelryStoreOptionDefaults,
  getStoreJewelryOptions,
  getStoreOptionListIds,
  getStoreOptionListMeta,
  getStoreOptionLists,
  legacyProductFormKeyForStoreList,
  mergeProductStoreListPatch,
  normalizeStoreOptionLists,
  productOptionsPatchForStoreList,
  resolveProductStoreListOverride,
  sanitizeStoreListOptions,
  serializeStoreOptionListsForSave,
  setJewelryStoreOptionDefaults,
  setStoreOptionLists,
  slugifyStoreListId,
  storeListShowsPrice,
} from './jewelryStoreOptionLists';

export const JEWELRY_PREFIX = 'j:';

/** @typedef {{ value: string, label: string, priceDelta?: number, image?: string }} JewelryOption */

/** @deprecated use getJewelryProfiles() — built-in only */
export const JEWELRY_PROFILES = BUILTIN_JEWELRY_PROFILES;

/** @param {string} profileId */
export function getProfileFieldDefs(profileId) {
  return getJewelryProfile(profileId)?.fields || [];
}

/** Core + optional fields turned on by default when admin has not configured yet. */
export function getDefaultEnabledFieldsForProfile(profileId) {
  return getProfileFieldDefs(profileId)
    .filter((f) => !f.optional || f.defaultEnabled)
    .map((f) => f.id);
}

/** @param {object} product */
export function getEnabledFieldIds(product) {
  const opts = normalizeProductJewelryOptions(product);
  return Array.isArray(opts.enabledFields) ? opts.enabledFields : [];
}

/** @param {object} field @param {object} product */
export function isJewelryFieldEnabled(field, product) {
  return getEnabledFieldIds(product).includes(field.id);
}

/** @param {object} field @param {object} product */
export function isJewelryFieldRequired(field, product) {
  const opts = getProductJewelryOptions(product);
  if (opts.requiredFields && field.id in opts.requiredFields) {
    return Boolean(opts.requiredFields[field.id]);
  }
  if (field.optional) return false;
  return Boolean(field.required);
}

/** @param {string} profileId @param {object} [existing] */
export function defaultCustomizationOptionsForProfile(profileId, existing = {}) {
  const base = existing && typeof existing === 'object' && !Array.isArray(existing) ? { ...existing } : {};
  return normalizeProductJewelryOptions({
    customization_profile: profileId,
    customization_options: {
      ...base,
      enabledFields: getDefaultEnabledFieldsForProfile(profileId),
      requiredFields: base.requiredFields && typeof base.requiredFields === 'object' ? base.requiredFields : {},
      configVersion: 2,
    },
  });
}

function resolveOptions(field, productOptions) {
  const fieldId = String(field?.id || '').trim();
  const customFieldOptions = productOptions?.fieldOptions?.[fieldId];
  if (Array.isArray(customFieldOptions) && customFieldOptions.length) {
    return sanitizeJewelryOptions(customFieldOptions);
  }

  const key = slugifyStoreListId(field.optionsKey);
  if (key) {
    const custom = resolveProductStoreListOverride(productOptions, key);
    if (custom?.length) return sanitizeJewelryOptions(custom);
    const storeOpts = getStoreJewelryOptions(key);
    if (storeOpts.length) return storeOpts;
  }
  return field.options || [];
}

/** @param {unknown} display */
export function normalizeJewelrySelectDisplay(display) {
  const v = String(display ?? '').trim().toLowerCase();
  return v === JEWELRY_SELECT_DISPLAY_SWATCHES ? JEWELRY_SELECT_DISPLAY_SWATCHES : JEWELRY_SELECT_DISPLAY_DROPDOWN;
}

/** @param {object} field @param {Record<string, object>} fieldConfig @param {JewelryOption[]} options */
export function resolveJewelrySelectDisplay(field, fieldConfig, options = []) {
  const cfg = fieldConfig?.[field.id];
  if (cfg?.display) return normalizeJewelrySelectDisplay(cfg.display);
  if (field.swatches || field.display === JEWELRY_SELECT_DISPLAY_SWATCHES) {
    return JEWELRY_SELECT_DISPLAY_SWATCHES;
  }
  if ((options || []).some((o) => String(o?.image || '').trim())) {
    return JEWELRY_SELECT_DISPLAY_SWATCHES;
  }
  return JEWELRY_SELECT_DISPLAY_DROPDOWN;
}

/** @param {object} field @param {Record<string, object>} fieldConfig */
export function jewelrySelectGalleryPreviewEnabled(field, fieldConfig) {
  const cfg = fieldConfig?.[field.id];
  if (cfg && 'galleryPreview' in cfg) return Boolean(cfg.galleryPreview);
  return Boolean(field.swatches || field.display === JEWELRY_SELECT_DISPLAY_SWATCHES);
}

/** @param {object} product */
export function getProductJewelryProfileId(product) {
  const id = String(product?.customization_profile || '').trim().toLowerCase();
  if (id && getJewelryProfile(id)) return id;
  return '';
}

/** @param {object} product */
export function productJewelryCustomizationEnabled(product) {
  return Boolean(getProductJewelryProfileId(product));
}

/** @param {object} product */
export function getProductJewelryOptions(product) {
  return normalizeProductJewelryOptions(product);
}

/**
 * Normalize API/DB customization_options (may be [], null, or legacy all-fields list).
 * @param {object} product
 */
export function normalizeProductJewelryOptions(product, { loose = false } = {}) {
  const profileId = getProductJewelryProfileId(product);
  if (!profileId) return {};

  const raw = product?.customization_options;
  let opts = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    opts = { ...raw };
  } else if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        opts = { ...parsed };
      }
    } catch {
      opts = {};
    }
  }

  const fieldDefs = getProfileFieldDefs(profileId);
  const valid = new Set(fieldDefs.map((f) => f.id));
  const coreIds = fieldDefs.filter((f) => !f.optional).map((f) => f.id);
  const optionalIds = fieldDefs.filter((f) => f.optional).map((f) => f.id);
  const allIds = fieldDefs.map((f) => f.id);

  let enabled = Array.isArray(opts.enabledFields)
    ? opts.enabledFields.filter((id) => valid.has(id))
    : [];

  // Missing/empty enabledFields → profile defaults (e.g. locket = photo only).
  if (enabled.length === 0) {
    enabled = getDefaultEnabledFieldsForProfile(profileId);
  } else if (
    optionalIds.length > 0 &&
    enabled.length === allIds.length &&
    optionalIds.every((id) => enabled.includes(id)) &&
    coreIds.every((id) => enabled.includes(id)) &&
    !opts.configVersion
  ) {
    // Legacy save before per-product toggles — treat as unset optional fields.
    enabled = getDefaultEnabledFieldsForProfile(profileId);
  }

  const requiredFields =
    opts.requiredFields && typeof opts.requiredFields === 'object' && !Array.isArray(opts.requiredFields)
      ? { ...opts.requiredFields }
      : {};

  for (const key of ['chainOptions', 'ringOptions', 'braceletOptions']) {
    if (Array.isArray(opts[key])) {
      const hasDraft = opts[key].some((row) => !String(row?.label ?? '').trim());
      if (!hasDraft) {
        const cleaned = sanitizeJewelryOptions(opts[key]);
        if (cleaned.length) opts[key] = cleaned;
        else delete opts[key];
      }
    }
  }

  if (opts.storeListOptions && typeof opts.storeListOptions === 'object' && !Array.isArray(opts.storeListOptions)) {
    const nextStoreListOptions = {};
    for (const [listId, rows] of Object.entries(opts.storeListOptions)) {
      if (!Array.isArray(rows)) continue;
      const hasDraft = rows.some((row) => !String(row?.label ?? '').trim());
      if (hasDraft) {
        nextStoreListOptions[listId] = rows;
        continue;
      }
      const cleaned = sanitizeJewelryOptions(rows);
      if (cleaned.length) nextStoreListOptions[listId] = cleaned;
    }
    if (Object.keys(nextStoreListOptions).length) opts.storeListOptions = nextStoreListOptions;
    else delete opts.storeListOptions;
  }

  if (opts.fieldOptions && typeof opts.fieldOptions === 'object' && !Array.isArray(opts.fieldOptions)) {
    const nextFieldOptions = {};
    for (const [fieldId, rows] of Object.entries(opts.fieldOptions)) {
      if (!valid.has(fieldId) || !Array.isArray(rows)) continue;
      const hasDraft = rows.some((row) => !String(row?.label ?? '').trim());
      if (hasDraft) {
        nextFieldOptions[fieldId] = rows;
        continue;
      }
      const cleaned = sanitizeJewelryOptions(rows);
      if (cleaned.length) nextFieldOptions[fieldId] = cleaned;
    }
    if (Object.keys(nextFieldOptions).length) opts.fieldOptions = nextFieldOptions;
    else delete opts.fieldOptions;
  }

  const fieldConfig = sanitizeFieldConfig(opts.fieldConfig, valid, { loose });

  const out = {
    ...opts,
    configVersion: 2,
    enabledFields: enabled,
    requiredFields,
  };
  if (Object.keys(fieldConfig).length) out.fieldConfig = fieldConfig;
  else delete out.fieldConfig;
  return out;
}

export const TEXT_FIELD_MAX_LENGTH_CAP = 500;
export const PHOTO_FIELD_MAX_FILES_CAP = 8;
export const PHOTO_FIELD_DEFAULT_MAX_FILES = 1;

export const JEWELRY_TEXT_CASE_MIXED = 'mixed';
export const JEWELRY_TEXT_CASE_UPPERCASE = 'uppercase';
export const JEWELRY_TEXT_CASE_LOWERCASE = 'lowercase';
export const JEWELRY_TEXT_CHARSET_ANY = 'any';
export const JEWELRY_TEXT_CHARSET_LETTERS = 'letters';
export const JEWELRY_TEXT_CHARSET_LETTERS_SPACES = 'letters_spaces';
export const JEWELRY_TEXT_CHARSET_ALPHANUM = 'alphanumeric';
export const JEWELRY_TEXT_CHARSET_ALPHANUM_SPACES = 'alphanumeric_spaces';
export const JEWELRY_TEXT_CHARSET_NUMBERS = 'numbers';

export const JEWELRY_SELECT_DISPLAY_DROPDOWN = 'dropdown';
export const JEWELRY_SELECT_DISPLAY_SWATCHES = 'swatches';

/** @param {unknown} textCase */
export function normalizeJewelryTextCase(textCase) {
  const v = String(textCase ?? '').trim().toLowerCase();
  if (v === JEWELRY_TEXT_CASE_UPPERCASE) return JEWELRY_TEXT_CASE_UPPERCASE;
  if (v === JEWELRY_TEXT_CASE_LOWERCASE) return JEWELRY_TEXT_CASE_LOWERCASE;
  return JEWELRY_TEXT_CASE_MIXED;
}

/** @param {string} value @param {unknown} textCase */
export function applyJewelryTextCase(value, textCase) {
  const text = String(value ?? '');
  const normalized = normalizeJewelryTextCase(textCase);
  if (normalized === JEWELRY_TEXT_CASE_UPPERCASE) return text.toUpperCase();
  if (normalized === JEWELRY_TEXT_CASE_LOWERCASE) return text.toLowerCase();
  return text;
}

/** @param {unknown} raw */
export function normalizeJewelryTextCharset(raw) {
  const v = String(raw ?? '').trim().toLowerCase();
  if (
    [
      JEWELRY_TEXT_CHARSET_LETTERS,
      JEWELRY_TEXT_CHARSET_LETTERS_SPACES,
      JEWELRY_TEXT_CHARSET_ALPHANUM,
      JEWELRY_TEXT_CHARSET_ALPHANUM_SPACES,
      JEWELRY_TEXT_CHARSET_NUMBERS,
    ].includes(v)
  ) {
    return v;
  }
  return JEWELRY_TEXT_CHARSET_ANY;
}

export function jewelryTextCharsetPattern(charset) {
  const mode = normalizeJewelryTextCharset(charset);
  if (mode === JEWELRY_TEXT_CHARSET_LETTERS) return /^[A-Za-z]+$/;
  if (mode === JEWELRY_TEXT_CHARSET_LETTERS_SPACES) return /^[A-Za-z ]+$/;
  if (mode === JEWELRY_TEXT_CHARSET_ALPHANUM) return /^[A-Za-z0-9]+$/;
  if (mode === JEWELRY_TEXT_CHARSET_ALPHANUM_SPACES) return /^[A-Za-z0-9 ]+$/;
  if (mode === JEWELRY_TEXT_CHARSET_NUMBERS) return /^[0-9]+$/;
  return null;
}

/** @param {unknown} raw */
export function normalizePhotoMaxFiles(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return PHOTO_FIELD_DEFAULT_MAX_FILES;
  return Math.min(n, PHOTO_FIELD_MAX_FILES_CAP);
}

/** @param {unknown} raw */
export function normalizePhotoValueList(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((u) => String(u || '').trim())
      .filter(Boolean)
      .slice(0, PHOTO_FIELD_MAX_FILES_CAP);
  }
  const one = String(raw || '').trim();
  return one ? [one] : [];
}

/**
 * Per-product overrides for text and select fields.
 * @param {unknown} raw @param {Set<string>} validIds
 * @returns {Record<string, { label?: string, placeholder?: string, helper?: string, minLength?: number, maxLength?: number, textCase?: string, charSet?: string, display?: string, galleryPreview?: boolean }>}
 */
/**
 * @param {unknown} raw
 * @param {Set<string>|null} validIds
 * @param {{ loose?: boolean }} [opts]  loose=true preserves mid-edit strings (spaces, empty) without trimming/dropping
 */
export function sanitizeFieldConfig(raw, validIds = null, { loose = false } = {}) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [fieldId, cfg] of Object.entries(raw)) {
    if (validIds && !validIds.has(fieldId)) continue;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) continue;
    const entry = {};
    const label = loose ? String(cfg.label ?? '') : String(cfg.label ?? '').trim();
    if (loose ? label !== undefined : label) entry.label = label.slice(0, 60);
    const placeholder = loose ? String(cfg.placeholder ?? '') : String(cfg.placeholder ?? '').trim();
    if (loose ? placeholder !== undefined : placeholder) entry.placeholder = placeholder.slice(0, 80);
    const helper = loose ? String(cfg.helper ?? '') : String(cfg.helper ?? '').trim();
    if (loose ? helper !== undefined : helper) entry.helper = helper.slice(0, 200);
    const max = parseInt(cfg.maxLength, 10);
    if (Number.isFinite(max) && max > 0) entry.maxLength = Math.min(max, TEXT_FIELD_MAX_LENGTH_CAP);
    const min = parseInt(cfg.minLength, 10);
    if (Number.isFinite(min) && min > 0) entry.minLength = Math.min(min, TEXT_FIELD_MAX_LENGTH_CAP);
    if (entry.minLength && entry.maxLength && entry.minLength > entry.maxLength) {
      entry.minLength = entry.maxLength;
    }
    const textCase = normalizeJewelryTextCase(cfg.textCase);
    if (textCase !== JEWELRY_TEXT_CASE_MIXED) entry.textCase = textCase;
    const charSet = normalizeJewelryTextCharset(cfg.charSet);
    if (charSet !== JEWELRY_TEXT_CHARSET_ANY) entry.charSet = charSet;
    if (cfg.display != null) {
      entry.display = normalizeJewelrySelectDisplay(cfg.display);
    }
    if ('galleryPreview' in cfg) entry.galleryPreview = Boolean(cfg.galleryPreview);
    if (Object.keys(entry).length) out[fieldId] = entry;
  }
  return out;
}

/** @deprecated use sanitizeFieldConfig */
export function sanitizeTextFieldConfig(raw, validIds = null, opts = {}) {
  return sanitizeFieldConfig(raw, validIds, opts);
}

/** @param {string} label @param {number} index */
export function suggestJewelryOptionValue(label, index = 0) {
  const trimmed = String(label || '').trim();
  if (!trimmed) return `option_${index + 1}`;
  const lower = trimmed.toLowerCase();
  if (lower.includes('no chain') || lower.includes('pendant only')) return 'none';
  if (lower.includes('adjustable')) return 'adjustable';
  const num = trimmed.match(/\d+/);
  if (num) return num[0];
  return (
    trimmed
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 32) || `option_${index + 1}`
  );
}

/** @param {JewelryOption} row @param {number} index */
export function normalizeJewelryOptionRow(row, index = 0) {
  const label = String(row?.label ?? '').trim();
  let value = String(row?.value ?? '').trim();
  if (!value && label) value = suggestJewelryOptionValue(label, index);
  if (!value) value = `option_${index + 1}`;
  const image = String(row?.image ?? '').trim();
  return {
    value,
    label,
    priceDelta: Number(row?.priceDelta) || 0,
    ...(image ? { image } : {}),
  };
}

/** @param {JewelryOption | null | undefined} row */
export function jewelryOptionRowHasContent(row) {
  return Boolean(String(row?.label ?? '').trim() || String(row?.image ?? '').trim());
}

/** @param {JewelryOption[] | null | undefined} options @param {{ strict?: boolean }} [opts] */
export function sanitizeJewelryOptions(options, { strict = true } = {}) {
  return (options || [])
    .map((row, index) => {
      const normalized = normalizeJewelryOptionRow(row, index);
      // Image-only swatches (upload before naming) get a default label so they persist and display.
      if (!normalized.label && normalized.image) {
        return { ...normalized, label: `Color ${index + 1}` };
      }
      return normalized;
    })
    .filter((row) => (strict ? row.label : jewelryOptionRowHasContent(row)));
}

/** @param {JewelryOption[] | null | undefined} options */
export function jewelryOptionsToLines(options) {
  return (options || [])
    .map((o) => {
      const delta = Number(o.priceDelta) || 0;
      return `${o.value}|${o.label}|${delta}`;
    })
    .join('\n');
}

/**
 * @param {string} text
 * @param {{ optionKey?: string }} [opts]
 */
export function jewelryLinesToOptions(text, { optionKey = '' } = {}) {
  const lines = String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  const out = [];
  for (const line of lines) {
    const parts = line.split('|').map((p) => p.trim());
    if (parts.length < 2 || !parts[0]) continue;
    const entry = { value: parts[0], label: parts[1] };
    if (parts.length >= 3 && parts[2] !== '') {
      entry.priceDelta = Number(parts[2]) || 0;
    } else if (optionKey === 'chain') {
      entry.priceDelta = 0;
    }
    out.push(entry);
  }
  return out.length ? out : null;
}

/** @param {object} product */
export function getJewelryProfileForProduct(product) {
  const id = getProductJewelryProfileId(product);
  if (!id) return null;
  return getJewelryProfile(id);
}

/** @param {object} product */
export function getJewelryFieldsForProduct(product) {
  const profile = getJewelryProfileForProduct(product);
  if (!profile) return [];
  const opts = getProductJewelryOptions(product);
  const enabled = new Set(getEnabledFieldIds(product));
  const fieldConfig = opts.fieldConfig && typeof opts.fieldConfig === 'object' ? opts.fieldConfig : {};
  return profile.fields
    .filter((f) => enabled.has(f.id))
    .map((f) => {
      const resolvedOptions = f.type === 'select' ? resolveOptions(f, opts) : undefined;
      const next = {
        ...f,
        required: isJewelryFieldRequired(f, product),
        options: resolvedOptions,
      };
      if (f.type === 'text') {
        const cfg = fieldConfig[f.id];
        const baseMax = parseInt(f.maxLength, 10);
        const baseMin = parseInt(f.minLength, 10);
        next.maxLength = Number.isFinite(baseMax) && baseMax > 0 ? Math.min(baseMax, TEXT_FIELD_MAX_LENGTH_CAP) : 200;
        next.minLength = Number.isFinite(baseMin) && baseMin > 0 ? Math.min(baseMin, next.maxLength) : 0;
        next.textCase = normalizeJewelryTextCase(f.textCase);
        next.charSet = normalizeJewelryTextCharset(f.charSet);
        if (cfg && typeof cfg === 'object') {
          if (cfg.label) next.label = cfg.label;
          if (cfg.placeholder) next.placeholder = cfg.placeholder;
          if (cfg.helper) next.hint = cfg.helper;
          else if (normalizeJewelryTextCase(cfg.textCase) === JEWELRY_TEXT_CASE_UPPERCASE) {
            next.hint = 'Engraved in capital letters.';
          }
          if (cfg.maxLength) next.maxLength = Math.min(parseInt(cfg.maxLength, 10) || next.maxLength, TEXT_FIELD_MAX_LENGTH_CAP);
          if (cfg.minLength) next.minLength = Math.min(parseInt(cfg.minLength, 10) || next.minLength, next.maxLength);
          next.textCase = normalizeJewelryTextCase(cfg.textCase);
          next.charSet = normalizeJewelryTextCharset(cfg.charSet);
        }
      }
      if (f.type === 'photo') {
        next.maxFiles = normalizePhotoMaxFiles(f.maxFiles);
      }
      if (f.type === 'select') {
        const cfg = fieldConfig[f.id];
        if (cfg?.label) next.label = cfg.label;
        next.display = resolveJewelrySelectDisplay(f, fieldConfig, resolvedOptions);
        next.galleryPreview = jewelrySelectGalleryPreviewEnabled(f, fieldConfig);
      }
      return next;
    });
}

/** Product cards: inline dropdowns only — photo/text/swatch fields belong on the product page. */
export function jewelryGridInlineEligible(product) {
  if (productMultiPieceEnabled(product)) return false;
  const fields = getJewelryFieldsForProduct(product);
  if (!fields.length) return false;
  return fields.every((f) => f.type === 'select' && f.display !== JEWELRY_SELECT_DISPLAY_SWATCHES) && fields.length <= 2;
}

/** @param {object} product @param {object} values */
export function resolveCustomizationGalleryImage(product, values) {
  const fields = getJewelryFieldsForProduct(product);
  const productOpts = getProductJewelryOptions(product);
  for (const field of fields) {
    if (field.type !== 'select' || !field.galleryPreview) continue;
    const val = String(values?.[field.id] || '').trim();
    if (!val) continue;
    const opts = field.options || resolveOptions(field, productOpts);
    const hit = (opts || []).find((o) => o.value === val);
    const image = String(hit?.image || '').trim();
    if (image) return image;
  }
  return '';
}

/** @param {object} values @param {object} product */
export function validateJewelryCustomization(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  const errors = {};
  for (const field of fields) {
    const val = values?.[field.id];
    if (field.type === 'photo') {
      const photos = normalizePhotoValueList(val);
      const maxFiles = normalizePhotoMaxFiles(field.maxFiles);
      const minFiles = field.required ? 1 : 0;
      if (photos.length < minFiles) {
        errors[field.id] = `Please upload at least ${minFiles} photo${minFiles === 1 ? '' : 's'}.`;
      } else if (photos.length > maxFiles) {
        errors[field.id] = `Upload up to ${maxFiles} photo${maxFiles === 1 ? '' : 's'}.`;
      }
      continue;
    }
    if (field.type === 'select') {
      const v = String(val || '').trim();
      if (field.required && !v) {
        errors[field.id] = `Choose ${field.label.toLowerCase()}.`;
      }
      continue;
    }
    const text = String(val || '').trim();
    if (field.required && !text) {
      errors[field.id] = `${field.label} is required.`;
    }
    if (text) {
      const minLength = parseInt(field.minLength, 10) || 0;
      if (minLength > 0 && text.length < minLength) {
        errors[field.id] = `Minimum ${minLength} characters.`;
        continue;
      }
      const pattern = jewelryTextCharsetPattern(field.charSet);
      if (pattern && !pattern.test(text)) {
        errors[field.id] = 'Contains unsupported characters.';
        continue;
      }
    }
    if (text && field.maxLength && text.length > field.maxLength) {
      errors[field.id] = `Max ${field.maxLength} characters.`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** @param {object} values @param {object} product */
export function jewelryCustomizationPriceDelta(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  let delta = 0;
  for (const field of fields) {
    if (field.type !== 'select') continue;
    const v = String(values?.[field.id] || '').trim();
    const opt = (field.options || resolveOptions(field, getProductJewelryOptions(product))).find(
      (o) => o.value === v
    );
    if (opt?.priceDelta) delta += Number(opt.priceDelta) || 0;
  }
  return delta;
}

function optionLabel(options, value) {
  const v = String(value || '').trim();
  const hit = (options || []).find((o) => o.value === v);
  return hit?.label || v;
}

/** @param {object} values @param {object} product */
export function formatJewelryCustomizationLabel(values, product) {
  const fields = getJewelryFieldsForProduct(product);
  const parts = [];
  for (const field of fields) {
    const val = values?.[field.id];
    if (!val) continue;
    if (field.type === 'photo') {
      const photos = normalizePhotoValueList(val);
      if (!photos.length) continue;
      parts.push(photos.length === 1 ? 'Photo uploaded' : `${photos.length} photos uploaded`);
      continue;
    }
    if (field.type === 'select') {
      const opts = field.options || resolveOptions(field, getProductJewelryOptions(product));
      parts.push(`${field.label}: ${optionLabel(opts, val)}`);
      continue;
    }
    parts.push(`${field.label}: ${applyJewelryTextCase(String(val).trim(), field.textCase)}`);
  }
  return parts.join(' · ');
}

/** @param {object} values @param {object} product */
export function serializeJewelryCustomization(values, product) {
  const profileId = getProductJewelryProfileId(product);
  const fields = getJewelryFieldsForProduct(product);
  const productOpts = getProductJewelryOptions(product);
  const normalizedValues = { ...values };
  const display = {};
  for (const field of fields) {
    const val = normalizedValues?.[field.id];
    if (val == null || val === '') continue;
    if (field.type === 'photo') {
      const photos = normalizePhotoValueList(val);
      if (!photos.length) continue;
      display[field.id] = photos.length === 1 ? 'Photo uploaded' : `${photos.length} photos uploaded`;
      normalizedValues[field.id] = photos.length === 1 ? photos[0] : photos;
      continue;
    }
    if (field.type === 'select') {
      const opts = field.options || resolveOptions(field, productOpts);
      display[field.id] = optionLabel(opts, val);
      continue;
    }
    display[field.id] = applyJewelryTextCase(String(val).trim(), field.textCase);
  }
  for (const field of fields) {
    if (field.type !== 'text') continue;
    const val = normalizedValues[field.id];
    if (val == null || val === '') continue;
    normalizedValues[field.id] = applyJewelryTextCase(String(val).trim(), field.textCase);
  }
  const payload = {
    v: 1,
    profile: profileId,
    values: normalizedValues,
    label: formatJewelryCustomizationLabel(normalizedValues, product),
    display,
  };
  return JEWELRY_PREFIX + JSON.stringify(payload);
}

/** @param {string} raw */
export function parseJewelryCustomization(raw) {
  const s = String(raw || '').trim();
  if (!s.startsWith(JEWELRY_PREFIX)) return null;
  try {
    const data = JSON.parse(s.slice(JEWELRY_PREFIX.length));
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

/** @param {string} raw @param {object} [product] */
export function parseCustomization(raw, product) {
  if (String(raw || '').trim().startsWith('m:')) {
    try {
      const piece = JSON.parse(String(raw).trim().slice(2));
      return {
        type: 'piece',
        data: piece,
        label: piece?.label || 'Multi-piece set',
      };
    } catch {
      return { type: 'piece', data: null, label: 'Multi-piece set' };
    }
  }
  const jewelry = parseJewelryCustomization(raw);
  if (jewelry) {
    return {
      type: 'jewelry',
      data: jewelry,
      values: jewelry.values || {},
      label: jewelry.label || formatJewelryCustomizationLabel(jewelry.values || {}, product),
    };
  }
  const letter = normalizePersonalizationLetter(raw);
  if (letter) {
    return { type: 'letter', letter, label: formatCharCustomizationLabel(letter) };
  }
  const text = String(raw || '').trim();
  if (text) return { type: 'text', text, label: text };
  return { type: 'none', label: '' };
}

/** @param {string} raw @param {object} [product] */
export function formatCustomizationDisplay(raw, product) {
  const p = parseCustomization(raw, product);
  return p.label || '';
}

/**
 * Structured customization for admin orders (photos, chain size, engraving, etc.).
 * @param {string} raw
 * @param {object} [product] Optional product row for per-product option lists (orders without display snapshot).
 * @returns {null | {
 *   profileId: string,
 *   profileLabel: string,
 *   summary: string,
 *   fields: Array<{ id: string, label: string, type: string, value: string, photoUrl?: string }>,
 *   photos: Array<{ url: string, fieldLabel: string }>,
 * }}
 */
export function describeJewelryCustomization(raw, product = null) {
  const jewelry = parseJewelryCustomization(raw);
  if (!jewelry) return null;

  const profile = getJewelryProfile(jewelry.profile);
  const values = jewelry.values && typeof jewelry.values === 'object' ? jewelry.values : {};
  const displayMap =
    jewelry.display && typeof jewelry.display === 'object' && !Array.isArray(jewelry.display)
      ? jewelry.display
      : null;

  const productLike = product || { customization_profile: jewelry.profile };
  const fieldDefs = product
    ? getJewelryFieldsForProduct(productLike)
    : (profile?.fields || []).filter((f) => {
        const val = values[f.id];
        return val != null && val !== '';
      });

  const productOpts = product ? getProductJewelryOptions(productLike) : {};
  const fields = [];
  const photos = [];

  for (const fieldDef of fieldDefs) {
    const val = values[fieldDef.id];
    if (val == null || val === '') continue;

    if (fieldDef.type === 'photo') {
      const urls = normalizePhotoValueList(val);
      if (!urls.length) continue;
      fields.push({
        id: fieldDef.id,
        label: fieldDef.label,
        type: 'photo',
        value: displayMap?.[fieldDef.id] || (urls.length === 1 ? 'Uploaded' : `${urls.length} uploaded`),
        photoUrl: urls[0],
      });
      for (const url of urls) photos.push({ url, fieldLabel: fieldDef.label });
      continue;
    }

    if (fieldDef.type === 'select') {
      const opts = fieldDef.options || resolveOptions(fieldDef, productOpts);
      fields.push({
        id: fieldDef.id,
        label: fieldDef.label,
        type: 'select',
        value: displayMap?.[fieldDef.id] || optionLabel(opts, val),
      });
      continue;
    }

    fields.push({
      id: fieldDef.id,
      label: fieldDef.label,
      type: fieldDef.type || 'text',
      value: displayMap?.[fieldDef.id] || String(val).trim(),
    });
  }

  const summaryProduct = product || { customization_profile: jewelry.profile };
  return {
    profileId: jewelry.profile,
    profileLabel: profile?.label || jewelry.profile || 'Custom jewelry',
    summary:
      jewelry.label ||
      formatJewelryCustomizationLabel(values, summaryProduct),
    fields,
    photos,
  };
}

/** Plain-text line for packing slips, emails, and exports. */
export function formatOrderItemCustomizationPlain(raw) {
  if (String(raw || '').trim().startsWith('m:')) {
    const { formatOrderItemPieceCustomizationPlain } = require('../pieceCustomization/pieceCustomization');
    return formatOrderItemPieceCustomizationPlain(raw);
  }

  const jewelry = describeJewelryCustomization(raw);
  if (jewelry) {
    const parts = jewelry.fields.map((f) => {
      if (f.type === 'photo') return `${f.label}: customer photo attached`;
      return `${f.label}: ${f.value}`;
    });
    return parts.join(' · ') || jewelry.summary || 'Customized';
  }

  const letter = normalizePersonalizationLetter(raw);
  if (letter) return formatCharCustomizationLabel(letter);

  const text = String(raw || '').trim();
  return text;
}

/** @param {string} raw */
export function orderItemHasCustomization(raw) {
  return Boolean(String(raw || '').trim());
}

/** @param {Array<{ customization_text?: string }>} items */
export function orderHasWorkshopPhotos(items) {
  return (items || []).some((it) => {
    const d = describeJewelryCustomization(it.customization_text);
    return Boolean(d?.photos?.length);
  });
}

/** @param {string} raw @param {object} product */
export function customizationUnitPriceDelta(raw, product) {
  if (String(raw || '').trim().startsWith('m:')) {
    const { parsePieceCustomization, pieceCustomizationPriceDelta } = require('../pieceCustomization/pieceCustomization');
    const piece = parsePieceCustomization(raw);
    if (piece?.mode === 'jewelry') {
      const values = (piece.pieces || []).map((p) => p.values || {});
      return pieceCustomizationPriceDelta(values, product);
    }
    return 0;
  }
  const p = parseCustomization(raw, product);
  if (p.type === 'jewelry') {
    return jewelryCustomizationPriceDelta(p.values, product);
  }
  return 0;
}

/** Whether product needs any pre-cart customization (jewelry, letter, or multi-piece set). */
export function productNeedsPreCartCustomization(product) {
  if (
    productMultiPieceEnabled(product) &&
    (productJewelryCustomizationEnabled(product) || productCharPersonalizationEnabled(product))
  ) {
    return true;
  }
  if (productJewelryCustomizationEnabled(product)) return true;
  return productCharPersonalizationEnabled(product);
}
